/* SpawnAnything — synthesized sound set. Pure JS DSP (no DOM, no assets): every sound is rendered once into a mono
   Float32Array that the WebAudio engine wraps in an AudioBuffer. Comic and punchy, nothing gory. */
export const SR = 32000;
const TAU = Math.PI * 2;

/** deterministic PRNG (mulberry32) so variants sound the same every session */
export function rng(seed) {
  let s = seed >>> 0;
  return () => { s = (s + 0x6D2B79F5) | 0; let t = Math.imul(s ^ (s >>> 15), 1 | s); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
}
const val = (f, t) => typeof f === 'function' ? f(t) : f;

/* ---------------- filters (in place) ---------------- */
/** one-pole low-pass; fc may be a function of time (seconds) */
function lp(a, sr, fc, from = 0) {
  let y = 0, k = 0;
  for (let i = from; i < a.length; i++) {
    if (((i - from) & 15) === 0) k = 1 - Math.exp(-TAU * Math.min(sr * 0.45, val(fc, i / sr)) / sr);
    y += k * (a[i] - y); a[i] = y;
  }
  return a;
}
function hp(a, sr, fc) {
  const k = 1 - Math.exp(-TAU * fc / sr); let y = 0;
  for (let i = 0; i < a.length; i++) { y += k * (a[i] - y); a[i] -= y; }
  return a;
}
/** RBJ band-pass (0 dB peak); f may be a function of time */
function bp(a, sr, f, q = 1) {
  let x1 = 0, x2 = 0, y1 = 0, y2 = 0, b0 = 0, b2 = 0, a1 = 0, a2 = 0;
  for (let i = 0; i < a.length; i++) {
    if ((i & 15) === 0) {
      const w = TAU * Math.min(sr * 0.45, Math.max(20, val(f, i / sr))) / sr, al = Math.sin(w) / (2 * q), a0 = 1 + al;
      b0 = al / a0; b2 = -al / a0; a1 = -2 * Math.cos(w) / a0; a2 = (1 - al) / a0;
    }
    const x = a[i], y = b0 * x + b2 * x2 - a1 * y1 - a2 * y2;
    x2 = x1; x1 = x; y2 = y1; y1 = y; a[i] = y;
  }
  return a;
}

/* ---------------- generators (add into out) ---------------- */
/**
 * Tone with pitch glide and exponential decay.
 * f0 -> f1 (exponential approach with time constant ft, or linear over ft when lin), wave sine|square|saw|tri,
 * at = start (s), att = attack (s), dec = decay time constant (s), len = hard length (s, faded), vib/vibHz = vibrato depth (ratio)/rate.
 */
function tone(out, sr, o) {
  const { f0, f1 = f0, ft = 0.05, lin = false, wave = 'sine', amp = 1, at = 0, att = 0.002, dec = 0.2, len = 0, vib = 0, vibHz = 6, fm = 0, fmHz = 0 } = o;
  const s0 = Math.floor(at * sr), n = Math.min(len ? Math.floor(len * sr) : 1e9, out.length - s0, Math.ceil((att + dec * 9.2) * sr));
  const kd = Math.exp(-1 / (dec * sr)), kf = Math.exp(-1 / (ft * sr));
  let ph = 0, ed = 1, ef = 1;
  for (let i = 0; i < n; i++) {
    const t = i / sr;
    let f = lin ? f0 + (f1 - f0) * Math.min(1, t / ft) : f1 + (f0 - f1) * ef;
    ef *= kf;
    if (vib) f *= 1 + vib * Math.sin(TAU * vibHz * t);
    if (fm) f += fm * fmHz * Math.sin(TAU * fmHz * t);
    ph += f / sr; ph -= Math.floor(ph);
    let w;
    if (wave === 'sine') w = Math.sin(TAU * ph);
    else if (wave === 'square') w = ph < 0.5 ? 0.8 : -0.8;
    else if (wave === 'saw') w = 2 * ph - 1;
    else w = 1 - 4 * Math.abs(ph - 0.5);
    let e = amp * ed * (t < att ? t / att : 1); ed *= kd;
    if (len && t > len - 0.01) e *= Math.max(0, (len - t) / 0.01);
    out[s0 + i] += w * e;
  }
}
/** noise burst with optional filter: lp/hp in Hz (lp may be a function), bp {f, q} */
function noise(out, sr, r, o) {
  const { amp = 1, at = 0, att = 0.001, dec = 0.1, len = 0, lp: L, hp: H, bp: B, am = 0, amHz = 0, brown = false } = o;
  const s0 = Math.floor(at * sr), n = Math.max(1, Math.min(len ? Math.floor(len * sr) : 1e9, out.length - s0, Math.ceil((att + dec * 9.2) * sr)));
  const tmp = new Float32Array(n), kd = Math.exp(-1 / (dec * sr));
  let ed = 1;
  let b = 0;
  for (let i = 0; i < n; i++) { const w = r() * 2 - 1; if (brown) { b = (b + w * 0.06) * 0.996; tmp[i] = b * 3; } else tmp[i] = w; }
  if (B) bp(tmp, sr, B.f, B.q || 1);
  if (H) hp(tmp, sr, H);
  if (L) lp(tmp, sr, L);
  for (let i = 0; i < n; i++) {
    const t = i / sr;
    let e = amp * ed * (t < att ? t / att : 1); ed *= kd;
    if (am) e *= 1 + am * Math.sin(TAU * amHz * t + r() * 0.3);
    if (len && t > len - 0.01) e *= Math.max(0, (len - t) / 0.01);
    out[s0 + i] += tmp[i] * e;
  }
}
/** Karplus-Strong plucked string */
function pluck(out, sr, r, { f, amp = 1, at = 0, damp = 0.996, bright = 0.5 }) {
  const N = Math.max(2, Math.round(sr / f)), d = new Float32Array(N);
  for (let i = 0; i < N; i++) d[i] = r() * 2 - 1;
  const s0 = Math.floor(at * sr);
  let p = 0;
  for (let i = s0; i < out.length; i++) {
    const cur = d[p], nx = d[p + 1 === N ? 0 : p + 1];
    out[i] += cur * amp; d[p] = damp * (bright * cur + (1 - bright) * 0.5 * (cur + nx)); p = p + 1 === N ? 0 : p + 1;
  }
}
/** sparse crackle: short filtered clicks spread over `span` seconds, thinning out */
function crackle(out, sr, r, { n, at = 0, span = 1, amp = 0.4, f0 = 700, f1 = 2600 }) {
  for (let k = 0; k < n; k++) {
    const t = at + Math.pow(r(), 1.6) * span, a = amp * (1 - (t - at) / (span * 1.2)) * (0.4 + r() * 0.6);
    noise(out, sr, r, { amp: a, at: t, dec: 0.004 + r() * 0.014, len: 0.05, bp: { f: f0 + r() * (f1 - f0), q: 2 } });
  }
}
/** slow random amplitude swell in place (crowd murmur, rumble) */
function wobble(a, sr, r, depth, hz) {
  const p1 = r() * TAU, p2 = r() * TAU;
  for (let i = 0; i < a.length; i++) { const t = i / sr; a[i] *= 1 - depth * 0.5 + depth * 0.5 * (Math.sin(TAU * hz * t + p1) * 0.6 + Math.sin(TAU * hz * 1.73 * t + p2) * 0.4); }
}
/** brass-like note (detuned saws through an enveloped low-pass) for stingers */
function brass(out, sr, r, { f, at, dur, amp = 0.5, vib = 0, bright = 2200 }) {
  const n = Math.min(out.length - Math.floor(at * sr), Math.floor((dur + 0.25) * sr)), tmp = new Float32Array(n);
  tone(tmp, sr, { f0: f, wave: 'saw', amp: 1, att: 0.03, dec: 99, len: dur + 0.25, vib, vibHz: 5.5 });
  tone(tmp, sr, { f0: f * 1.004, wave: 'saw', amp: 0.7, att: 0.03, dec: 99, len: dur + 0.25, vib, vibHz: 5.5 });
  lp(tmp, sr, t => 300 + bright * Math.min(1, t / 0.05) * (t < dur ? 1 : Math.exp(-(t - dur) / 0.08)));
  const s0 = Math.floor(at * sr);
  for (let i = 0; i < n; i++) { const t = i / sr, e = t < dur ? 1 : Math.exp(-(t - dur) / 0.07); out[s0 + i] += tmp[i] * amp * e; }
}
function normalize(a, peak) {
  let m = 0; for (let i = 0; i < a.length; i++) { const v = Math.abs(a[i]); if (v > m) m = v; }
  if (m > 0) { const k = peak / m; for (let i = 0; i < a.length; i++) a[i] *= k; }
  return a;
}
function fadeEdges(a, sr) {
  const n = Math.min(a.length >> 1, Math.floor(sr * 0.001)), m = Math.min(a.length >> 1, Math.floor(sr * 0.02));
  for (let i = 0; i < n; i++) a[i] *= i / n;
  for (let i = 0; i < m; i++) a[a.length - 1 - i] *= i / m;
  return a;
}

/* ---------------- the sound set: name -> { dur, peak, loop?, render(out, sr, r, variant) } ---------------- */
export const SOUNDS = {
  // melee
  clash: { dur: 0.5, variants: 3, render(o, sr, r) {
    const f = 560 + r() * 280, P = [1, 2.76, 5.4, 8.93, 13.34], A = [1, 0.7, 0.5, 0.35, 0.2], D = [0.3, 0.2, 0.13, 0.08, 0.05];
    P.forEach((p, i) => tone(o, sr, { f0: f * p * (1 + (r() - 0.5) * 0.01), amp: A[i] * 0.5, dec: D[i] }));
    noise(o, sr, r, { amp: 0.8, dec: 0.004, hp: 2000 });
    noise(o, sr, r, { amp: 0.25, dec: 0.07, bp: { f: 6000, q: 2 } });
  } },
  punch: { dur: 0.24, variants: 2, render(o, sr, r) {
    tone(o, sr, { f0: 150 + r() * 40, f1: 48, ft: 0.03, dec: 0.07, amp: 1 });
    noise(o, sr, r, { amp: 0.7, dec: 0.025, lp: 1700 });
  } },
  bonk: { dur: 0.3, render(o, sr, r) {
    tone(o, sr, { f0: 130, f1: 55, ft: 0.03, dec: 0.06, amp: 0.8 });
    noise(o, sr, r, { amp: 0.6, dec: 0.04, bp: { f: 720, q: 4 } });
    tone(o, sr, { f0: 430, dec: 0.05, amp: 0.35 });
  } },
  whoosh: { dur: 0.34, render(o, sr, r) {
    const d = 0.32;
    noise(o, sr, r, { amp: 1, dec: 99, len: d, bp: { f: t => 700 + 2200 * Math.sin(Math.PI * Math.min(1, t / d)), q: 1.4 } });
    for (let i = 0; i < o.length; i++) { const t = i / sr; o[i] *= t < d ? Math.sin(Math.PI * t / d) ** 2 : 0; }
  } },
  bow: { dur: 0.45, render(o, sr, r) {
    pluck(o, sr, r, { f: 196, amp: 0.55, damp: 0.993 });
    noise(o, sr, r, { amp: 0.5, at: 0.02, dec: 0.12, len: 0.3, bp: { f: t => 2600 - 4000 * t, q: 1.3 } });
  } },
  // guns
  rifle: { dur: 0.6, variants: 2, render(o, sr, r) {
    noise(o, sr, r, { amp: 1, dec: 0.004 });
    noise(o, sr, r, { amp: 0.8, dec: 0.035, lp: 3500 });
    tone(o, sr, { f0: 110, f1: 45, ft: 0.04, dec: 0.06, amp: 0.7 });
    noise(o, sr, r, { amp: 0.22, at: 0.02, dec: 0.2, lp: 900 });
  } },
  // pistol: the rifle voice played higher (the recorded pistol in samples.js replaces it once loaded)
  pistol: { dur: 0.46, variants: 2, render(o, sr, r) { SOUNDS.rifle.render(o, sr, r); } },
  musket: { dur: 1.0, render(o, sr, r) {
    noise(o, sr, r, { amp: 0.8, dec: 0.006 });
    noise(o, sr, r, { amp: 0.9, dec: 0.07, lp: 2200 });
    tone(o, sr, { f0: 80, f1: 35, ft: 0.06, dec: 0.12, amp: 0.8 });
    noise(o, sr, r, { amp: 0.35, at: 0.03, dec: 0.4, lp: 600 });
    noise(o, sr, r, { amp: 0.2, at: 0.01, dec: 0.25, bp: { f: 400, q: 1 } });
  } },
  mg: { dur: 0.26, variants: 2, render(o, sr, r) {
    noise(o, sr, r, { amp: 0.9, dec: 0.003 });
    noise(o, sr, r, { amp: 0.7, dec: 0.02, lp: 5000 });
    tone(o, sr, { f0: 130, f1: 60, ft: 0.02, dec: 0.035, amp: 0.5 });
    noise(o, sr, r, { amp: 0.15, at: 0.01, dec: 0.06, lp: 1200 });
  } },
  cannon: { dur: 1.8, sr: 22050, render(o, sr, r) {
    noise(o, sr, r, { amp: 1, dec: 0.008 });
    tone(o, sr, { f0: 75, f1: 28, ft: 0.15, dec: 0.35, amp: 1 });
    noise(o, sr, r, { amp: 0.9, dec: 0.45, lp: t => 150 + 1650 * Math.exp(-t / 0.2) });
    noise(o, sr, r, { amp: 0.35, at: 0.05, dec: 0.7, lp: 300 });
  } },
  rocket: { dur: 1.0, render(o, sr, r) {
    noise(o, sr, r, { amp: 0.4, dec: 0.03, hp: 2000 });
    noise(o, sr, r, { amp: 1, att: 0.04, dec: 0.45, bp: { f: t => 500 + 1500 * Math.min(1, t / 0.5), q: 0.8 } });
    tone(o, sr, { f0: 90, f1: 70, dec: 0.3, amp: 0.3 });
  } },
  laser: { dur: 0.26, variants: 2, render(o, sr, r) {
    const k = 0.9 + r() * 0.2;
    tone(o, sr, { f0: 2200 * k, f1: 380 * k, ft: 0.05, wave: 'square', dec: 0.09, amp: 0.5 });
    tone(o, sr, { f0: 3300 * k, f1: 600 * k, ft: 0.04, dec: 0.05, amp: 0.3 });
    lp(o, sr, 7000);
  } },
  plasma: { dur: 0.5, render(o, sr, r) {
    tone(o, sr, { f0: 900, f1: 200, ft: 0.02, dec: 0.05, amp: 0.3 });
    tone(o, sr, { f0: 240, f1: 120, ft: 0.15, att: 0.01, dec: 0.2, amp: 0.8, fm: 3.5, fmHz: 70 });
    noise(o, sr, r, { amp: 0.15, dec: 0.1, hp: 4000 });
  } },
  railgun: { dur: 1.2, render(o, sr, r) {
    tone(o, sr, { f0: 600, f1: 3400, ft: 0.12, lin: true, wave: 'saw', att: 0.11, dec: 99, len: 0.12, amp: 0.25 });
    noise(o, sr, r, { amp: 1, at: 0.12, dec: 0.006 });
    noise(o, sr, r, { amp: 0.6, at: 0.12, dec: 0.05, hp: 3000 });
    [[2400, 0.5, 0.3], [3610, 0.35, 0.2], [5220, 0.25, 0.15]].forEach(([f, d, a]) => tone(o, sr, { f0: f, at: 0.12, dec: d, amp: a }));
    tone(o, sr, { f0: 90, f1: 40, at: 0.12, ft: 0.05, dec: 0.15, amp: 0.8 });
  } },
  flame: { dur: 0.7, render(o, sr, r) {
    noise(o, sr, r, { amp: 1, att: 0.05, dec: 0.35, lp: 1000, am: 0.5, amHz: 23 });
    noise(o, sr, r, { amp: 0.6, att: 0.05, dec: 0.4, lp: 280, brown: true });
    crackle(o, sr, r, { n: 22, at: 0.03, span: 0.55, amp: 0.35, f0: 1800, f1: 5000 });
  } },
  gas: { dur: 0.9, render(o, sr, r) {
    noise(o, sr, r, { amp: 0.6, att: 0.08, dec: 0.35, hp: 2500 });
    tone(o, sr, { f0: 200, att: 0.01, at: 0.02, dec: 0.2, amp: 0.3, vib: 0.3, vibHz: 18 });
  } },
  // explosions
  expl_small: { dur: 1.2, variants: 2, render(o, sr, r) {
    noise(o, sr, r, { amp: 0.9, dec: 0.006 });
    tone(o, sr, { f0: 90, f1: 32, ft: 0.08, dec: 0.2, amp: 1 });
    noise(o, sr, r, { amp: 1, dec: 0.3, lp: t => 250 + 2250 * Math.exp(-t / 0.15) });
    crackle(o, sr, r, { n: 10, at: 0.05, span: 0.6, amp: 0.25 });
  } },
  expl_big: { dur: 2.6, sr: 22050, render(o, sr, r) {
    noise(o, sr, r, { amp: 1, dec: 0.01 });
    tone(o, sr, { f0: 60, f1: 24, ft: 0.2, dec: 0.5, amp: 1 });
    noise(o, sr, r, { amp: 1, dec: 0.7, lp: t => 120 + 1680 * Math.exp(-t / 0.3) });
    noise(o, sr, r, { amp: 0.5, at: 0.05, dec: 1.0, lp: 200, brown: true });
    crackle(o, sr, r, { n: 40, at: 0.08, span: 1.2, amp: 0.3 });
  } },
  nuke: { dur: 6, sr: 22050, peak: 0.95, render(o, sr, r) {
    tone(o, sr, { f0: 45, f1: 18, ft: 0.6, dec: 1.4, amp: 1 });
    noise(o, sr, r, { amp: 1, att: 0.02, dec: 1.8, lp: t => 60 + 1140 * Math.exp(-t / 0.6) });
    const rum = new Float32Array(o.length);
    noise(rum, sr, r, { amp: 0.8, att: 0.3, dec: 2.5, lp: 120, brown: true });
    wobble(rum, sr, r, 0.7, 0.6);
    for (let i = 0; i < o.length; i++) o[i] += rum[i];
    crackle(o, sr, r, { n: 120, at: 0.2, span: 3, amp: 0.25, f0: 400, f1: 1800 });
  } },
  // units, buildings
  poof: { dur: 0.3, variants: 2, render(o, sr, r) {
    noise(o, sr, r, { amp: 0.7, att: 0.005, dec: 0.07, bp: { f: 800 + r() * 300, q: 1 } });
    tone(o, sr, { f0: 300, f1: 900 + r() * 200, ft: 0.02, dec: 0.05, amp: 0.45 });
  } },
  bhit: { dur: 0.3, render(o, sr, r) {
    noise(o, sr, r, { amp: 0.9, dec: 0.05, bp: { f: 500, q: 2 } });
    tone(o, sr, { f0: 150, f1: 90, dec: 0.08, amp: 0.6 });
    noise(o, sr, r, { amp: 0.2, dec: 0.01, hp: 2000 });
  } },
  collapse: { dur: 2.6, sr: 22050, render(o, sr, r) {
    noise(o, sr, r, { amp: 1, att: 0.05, dec: 0.9, lp: 250, brown: true });
    tone(o, sr, { f0: 55, f1: 30, dec: 0.5, amp: 0.6 });
    crackle(o, sr, r, { n: 60, at: 0.02, span: 1.8, amp: 0.45, f0: 600, f1: 2500 });
  } },
  spawn: { dur: 0.6, render(o, sr, r) {
    [784, 988, 1175, 1568].forEach((f, i) => { tone(o, sr, { f0: f, at: i * 0.06, dec: 0.18, amp: 0.4 }); tone(o, sr, { f0: f * 2, at: i * 0.06, dec: 0.08, amp: 0.1 }); });
    noise(o, sr, r, { amp: 0.05, dec: 0.2, hp: 6000 });
  } },
  possess: { dur: 0.65, render(o, sr, r) {
    const d = 0.45;
    const w = new Float32Array(o.length);
    noise(w, sr, r, { amp: 0.8, dec: 99, len: d, bp: { f: t => 400 + 3100 * t / d, q: 2 } });
    for (let i = 0; i < w.length; i++) { const t = i / sr; o[i] += w[i] * (t < d ? Math.sin(Math.PI * t / d) : 0); }
    tone(o, sr, { f0: 280, f1: 840, ft: 0.35, lin: true, att: 0.02, dec: 0.3, amp: 0.4 });
    tone(o, sr, { f0: 1320, at: 0.3, dec: 0.2, amp: 0.25 });
  } },
  click: { dur: 0.06, peak: 0.6, render(o, sr, r) {
    tone(o, sr, { f0: 1700, dec: 0.012, amp: 0.6 });
    noise(o, sr, r, { amp: 0.2, dec: 0.003, hp: 3000 });
  } },
  // tools
  whistle: { dur: 1.2, peak: 0.6, render(o, sr) {
    tone(o, sr, { f0: 1900, f1: 650, ft: 1.1, lin: true, att: 0.9, dec: 99, len: 1.15, amp: 0.4, vib: 0.01, vibHz: 7 });
  } },
  twang: { dur: 0.9, render(o, sr, r) {
    pluck(o, sr, r, { f: 82, amp: 0.8, damp: 0.995, bright: 0.3 });
    noise(o, sr, r, { amp: 0.3, dec: 0.08, bp: { f: 300, q: 6 } });
    noise(o, sr, r, { amp: 0.4, at: 0.05, dec: 0.2, len: 0.5, bp: { f: t => 1600 - 1800 * t, q: 1.2 } });
  } },
  beam: { dur: 2.0, render(o, sr, r) {
    tone(o, sr, { f0: 120, f1: 900, ft: 0.5, lin: true, wave: 'saw', att: 0.45, dec: 99, len: 0.5, amp: 0.25 });
    noise(o, sr, r, { amp: 0.8, at: 0.5, dec: 0.02 });
    const z = new Float32Array(o.length);
    tone(z, sr, { f0: 110, wave: 'saw', at: 0.5, att: 0.01, dec: 0.6, amp: 0.4 });
    tone(z, sr, { f0: 111.5, wave: 'saw', at: 0.5, att: 0.01, dec: 0.6, amp: 0.4 });
    noise(z, sr, r, { amp: 0.3, at: 0.5, dec: 0.6, bp: { f: 1800, q: 1 } });
    lp(z, sr, 4000);
    for (let i = 0; i < o.length; i++) o[i] += z[i];
  } },
  honk: { dur: 0.4, render(o, sr) {
    tone(o, sr, { f0: 850, f1: 1150, ft: 0.08, wave: 'tri', att: 0.01, dec: 0.2, amp: 0.5, vib: 0.08, vibHz: 30 });
    lp(o, sr, 4000);
  } },
  // ambience bed (seamless loop)
  ambience: { dur: 4, sr: 22050, loop: true, peak: 0.7, render(o, sr, r) {
    const n = o.length, x = Math.floor(0.5 * sr), tmp = new Float32Array(n + x);
    noise(tmp, sr, r, { amp: 0.6, dec: 99, lp: 350, brown: true });
    const m1 = new Float32Array(n + x), m2 = new Float32Array(n + x);
    noise(m1, sr, r, { amp: 0.5, dec: 99, bp: { f: 600, q: 1.2 } }); wobble(m1, sr, r, 0.8, 0.7);
    noise(m2, sr, r, { amp: 0.2, dec: 99, bp: { f: 1500, q: 2 } }); wobble(m2, sr, r, 0.9, 1.3);
    for (let i = 0; i < n + x; i++) tmp[i] += m1[i] + m2[i];
    for (let i = 0; i < n; i++) o[i] = tmp[i];
    for (let i = 0; i < x; i++) { const k = i / x; o[i] = tmp[i] * k + tmp[n + i] * (1 - k); } // crossfade tail into head
  } },
  // music instruments (pitched ones are rendered at a reference pitch and transposed by playbackRate)
  kick: { dur: 0.35, render(o, sr, r) { tone(o, sr, { f0: 150, f1: 45, ft: 0.04, dec: 0.14, amp: 1 }); noise(o, sr, r, { amp: 0.3, dec: 0.003, hp: 3000 }); } },
  snare: { dur: 0.3, render(o, sr, r) { noise(o, sr, r, { amp: 0.8, dec: 0.1, bp: { f: 2000, q: 0.7 } }); tone(o, sr, { f0: 190, dec: 0.06, amp: 0.4 }); } },
  hat: { dur: 0.08, peak: 0.5, render(o, sr, r) { noise(o, sr, r, { amp: 0.5, dec: 0.02, hp: 7000 }); } },
  bass: { dur: 0.5, render(o, sr) { // A2
    tone(o, sr, { f0: 110, wave: 'saw', att: 0.005, dec: 0.3, amp: 0.8 }); tone(o, sr, { f0: 55, att: 0.005, dec: 0.3, amp: 0.4 });
    lp(o, sr, t => 200 + 1400 * Math.exp(-t / 0.06));
  } },
  pluck: { dur: 0.7, render(o, sr, r) { pluck(o, sr, r, { f: 440, amp: 0.6, damp: 0.994 }); tone(o, sr, { f0: 440, wave: 'tri', dec: 0.25, amp: 0.3 }); } }, // A4
  pad: { dur: 2.6, sr: 22050, peak: 0.6, render(o, sr) { // A3, soft attack and release
    [1, 1.006, 0.994].forEach(k => tone(o, sr, { f0: 220 * k, wave: 'saw', att: 0.4, dec: 99, amp: 0.3 }));
    lp(o, sr, 1400);
    for (let i = 0; i < o.length; i++) { const t = i / sr; if (t > 1.9) o[i] *= Math.max(0, 1 - (t - 1.9) / 0.7); }
  } },
  // stingers
  victory: { dur: 2.6, render(o, sr, r) {
    [[523, 0, 0.13], [659, 0.15, 0.13], [784, 0.3, 0.13]].forEach(([f, at, dur]) => brass(o, sr, r, { f, at, dur, amp: 0.45 }));
    [1046, 784, 659].forEach((f, i) => brass(o, sr, r, { f, at: 0.45, dur: 1.5, amp: i ? 0.25 : 0.45, vib: 0.006 }));
    tone(o, sr, { f0: 98, f1: 90, dec: 0.4, amp: 0.6 }); tone(o, sr, { f0: 98, f1: 90, at: 0.45, dec: 0.6, amp: 0.7 });
    noise(o, sr, r, { amp: 0.25, at: 0.45, dec: 0.5, hp: 5000 });
  } },
  defeat: { dur: 2.8, render(o, sr, r) { // comic "wah wah wah waaah"
    [[392, 0, 0.4], [370, 0.5, 0.4], [349, 1.0, 0.4]].forEach(([f, at, dur]) => brass(o, sr, r, { f, at, dur, amp: 0.5, bright: 1100 }));
    brass(o, sr, r, { f: 330, at: 1.5, dur: 1.0, amp: 0.55, vib: 0.03, bright: 1100 });
  } },
};

/** render a sound into a fresh mono buffer (normalized, de-clicked) */
/** sample rate a sound is rendered at (low rumbles use a lower rate: cheaper to render, no audible loss) */
export const soundRate = name => (SOUNDS[name] && SOUNDS[name].sr) || SR;
export function renderSound(name, sr = soundRate(name), variant = 0) {
  const S = SOUNDS[name]; if (!S) throw new Error('unknown sound ' + name);
  const out = new Float32Array(Math.ceil(S.dur * sr));
  S.render(out, sr, rng(0x9e3779b1 ^ (name.length * 7919 + name.charCodeAt(0) * 31 + variant * 104729)), variant);
  normalize(out, S.peak || 0.9);
  return S.loop ? out : fadeEdges(out, sr);
}
